import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import ja from '../locales/ja.json';
import en from '../locales/en.json';
import zhCN from '../locales/zh-CN.json';
import fr from '../locales/fr.json';
import es from '../locales/es.json';
import de from '../locales/de.json';
import pt from '../locales/pt.json';
import ru from '../locales/ru.json';

/** トップページと言語ページで同じ Home を使うため、ここで一元定義。ルート・検出の両方で使用。 */
export const SUPPORTED_LOCALES = [
  'ja',
  'en',
  'zh-CN',
  'fr',
  'es',
  'de',
  'pt',
  'ru',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** UI 言語が日本語か（`ja` / `ja-JP` 等）。日本固有の法令・義務注記の表示判定用 */
export function isJapaneseUiLanguage(language?: string | null): boolean {
  const lang = String(language || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
  return lang === 'ja' || lang.startsWith('ja-');
}

const resources = {
  ja: { translation: ja },
  en: { translation: en },
  'zh-CN': { translation: zhCN },
  fr: { translation: fr },
  es: { translation: es },
  de: { translation: de },
  pt: { translation: pt },
  ru: { translation: ru },
};

// ブラウザの言語をサポートされている言語コードにマッピング（SUPPORTED_LOCALES と整合）
const supportedLanguagesList = [...SUPPORTED_LOCALES];
const mapBrowserLanguageToSupported = (browserLang: string): string | null => {
  if (!browserLang) return null;

  if (supportedLanguagesList.includes(browserLang as SupportedLocale)) {
    return browserLang;
  }

  const langCode = browserLang.split('-')[0].toLowerCase();
  if (langCode === 'zh') return 'zh-CN';
  if (supportedLanguagesList.includes(langCode as SupportedLocale)) return langCode;

  return null;
};

/**
 * 無印 URL が ja / x-default であり、かつ言語付き URL（/en/...）と対になる公開パス。
 * Googlebot は navigator が en になりやすく、ここを抜くと /gaisuke の canonical が /en/gaisuke になる。
 *
 * privacy / terms / login 等は言語付き URL が無いので含めない
 *（含めると App の path sync が LanguageSwitcher の選択を上書きする）。
 */
export const BARE_PUBLIC_FIRST_SEGMENTS = new Set([
  'help',
  'contact',
  'gaisuke',
  'keisuke',
  'labo-suke',
  'arena',
  'keiba-arena',
]);

/** URL を正本にした公開ページのロケール。保護ルート等は undefined。 */
export function localeFromPublicPathname(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'ja';
  const first = segments[0];
  if (SUPPORTED_LOCALES.includes(first as SupportedLocale)) {
    return first;
  }
  if (BARE_PUBLIC_FIRST_SEGMENTS.has(first)) return 'ja';
  return undefined;
}

const pathLocaleDetector = {
  name: 'pathLocale' as const,
  lookup(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    return localeFromPublicPathname(window.location.pathname);
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(pathLocaleDetector);

i18n
  .use(languageDetector) // ブラウザの言語設定を自動検出（pathLocale を追加済み）
  .use(initReactI18next) // react-i18nextを初期化
  .init({
    resources,
    // lngは設定しない（LanguageDetectorがpath/localStorage/cookie/ブラウザ言語から検出する）
    fallbackLng: 'en', // フォールバック言語（言語が検出できない場合は英語を使用）
    defaultNS: 'translation',
    returnNull: false, // 翻訳が見つからない場合は空文字列を返す（キーを返さない）
    returnEmptyString: false, // 翻訳が見つからない場合はフォールバック言語を使用
    // キーが見つからない場合の動作を制御
    keySeparator: '.', // キーの区切り文字（デフォルト）
    nsSeparator: ':', // ネームスペースの区切り文字（デフォルト）
    // 翻訳デバッグが必要なときだけ .env に VITE_I18N_DEBUG=true（開発時）
    debug: import.meta.env.DEV && import.meta.env.VITE_I18N_DEBUG === 'true',
    interpolation: {
      escapeValue: false, // Reactは既にXSS対策されているため
    },
    // 翻訳が見つからない場合のハンドラー
    missingKeyHandler: (lng: string[], ns: string, key: string, fallbackValue: string) => {
      console.warn(`[i18n] Missing translation key: ${key} for language: ${lng.join(', ')}`);
      // フォールバック言語を使用
      return fallbackValue || key;
    },
    detection: {
      // 言語検出の設定（path を最優先: /en で開いたとき最初から英語で表示）
      // 優先順位: 1. URLパス 2. localStorage 3. Cookie 4. ブラウザの言語 5. HTMLタグ
      order: ['pathLocale', 'localStorage', 'cookie', 'navigator', 'htmlTag'],
      caches: ['localStorage', 'cookie'],
      lookupLocalStorage: 'i18nextLng',
      lookupCookie: 'preferred_language',
      cookieMinutes: 60 * 24 * 30, // 30日間
      // カスタム言語マッピング関数（ブラウザの言語をサポートされている言語に変換）
      convertDetectedLanguage: (lng: string) => {
        const mapped = mapBrowserLanguageToSupported(lng);
        return mapped || 'en';
      },
    },
  });

// 開発モードでViteのHMRを使用して翻訳リソースをリロード
// 各JSONファイルの変更を受け取り、i18nextのリソースを更新
if (import.meta.hot) {
  // 日本語の翻訳ファイルの変更を受け取る
  import.meta.hot.accept('../locales/ja.json', (newModule) => {
    if (newModule) {
      i18n.addResourceBundle('ja', 'translation', newModule.default, true, true);
      console.log('[i18n] Japanese translation resources reloaded');
    }
  });

  // 英語の翻訳ファイルの変更を受け取る
  import.meta.hot.accept('../locales/en.json', (newModule) => {
    if (newModule) {
      i18n.addResourceBundle('en', 'translation', newModule.default, true, true);
      console.log('[i18n] English translation resources reloaded');
    }
  });

  import.meta.hot.accept('../locales/zh-CN.json', (newModule) => {
    if (newModule) {
      i18n.addResourceBundle('zh-CN', 'translation', newModule.default, true, true);
      console.log('[i18n] Chinese translation resources reloaded');
    }
  });

  import.meta.hot.accept('../locales/fr.json', (newModule) => {
    if (newModule) {
      i18n.addResourceBundle('fr', 'translation', newModule.default, true, true);
      console.log('[i18n] French translation resources reloaded');
    }
  });

  import.meta.hot.accept('../locales/es.json', (newModule) => {
    if (newModule) {
      i18n.addResourceBundle('es', 'translation', newModule.default, true, true);
      console.log('[i18n] Spanish translation resources reloaded');
    }
  });

  import.meta.hot.accept('../locales/de.json', (newModule) => {
    if (newModule) {
      i18n.addResourceBundle('de', 'translation', newModule.default, true, true);
      console.log('[i18n] German translation resources reloaded');
    }
  });

  import.meta.hot.accept('../locales/pt.json', (newModule) => {
    if (newModule) {
      i18n.addResourceBundle('pt', 'translation', newModule.default, true, true);
      console.log('[i18n] Portuguese translation resources reloaded');
    }
  });

  import.meta.hot.accept('../locales/ru.json', (newModule) => {
    if (newModule) {
      i18n.addResourceBundle('ru', 'translation', newModule.default, true, true);
      console.log('[i18n] Russian translation resources reloaded');
    }
  });
}

export default i18n;
